import React, { useState, useEffect, useCallback } from "react";
import {
    Plus,
    Search,
    Edit2,
    Trash2,
    MapPin,
    Filter,
    CheckCircle2,
    Clock,
    Ticket,
    AlertCircle,
    Loader2,
    Eye
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEvents } from "../../services/admin/EventsServices";

import ViewEventModal from "../../components/admin/ViewEventModal";

export default function EventsPage() {
    const navigate = useNavigate();
    const { fetchAllEvents, deleteEvent, loading } = useEvents();

    const [eventsList, setEventsList] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [viewModal, setViewModal] = useState({ isOpen: false, event: null });


    const loadEvents = useCallback(async () => {
        try {
            const data = await fetchAllEvents();
            if (Array.isArray(data)) {
                setEventsList(data);
            }
        } finally {
            setIsInitialLoad(false);
        }
    }, [fetchAllEvents]);

    useEffect(() => {
        loadEvents();
    }, [loadEvents]);

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this event?")) {
            const result = await deleteEvent(id);
            if (result && result.status === "success") {
                setEventsList(prev => prev.filter(event => event.id !== id));
            }
        }
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'On Sale':
                return 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400';
            case 'Sold Out':
                return 'bg-black text-white dark:bg-white dark:text-black';
            case 'Presale':
                return 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400';
            case 'Coming Soon':
                return 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400';
            case 'Free':
                return 'bg-jazz-brown text-white';
            case 'Completed':
            case 'Done':
                return 'bg-black/5 text-black/40 dark:bg-white/5 dark:text-white/40';
            default:
                return 'bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400';
        }
    };

    const filteredEvents = (Array.isArray(eventsList) ? eventsList : []).filter(event =>
        (event.title || '').toLowerCase().includes((searchTerm || '').toLowerCase()) ||
        (event.venue || '').toLowerCase().includes((searchTerm || '').toLowerCase()) ||
        (event.location || '').toLowerCase().includes((searchTerm || '').toLowerCase())
    );

    return (
        <div className="p-6 lg:p-10 max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-normal text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                            Tour Dates & Events
                        </h1>
                        <p className="text-black dark:text-white/50 text-sm mt-1">
                            Manage your upcoming concerts, festivals, and appearances.
                        </p>
                    </div>
                    <button
                        onClick={() => navigate("/admin/events/add")}
                        className="flex items-center justify-center gap-2 bg-black text-white px-6 py-3 rounded-xl text-sm font-bold tracking-widest hover:bg-jazz-brown transition-all w-full md:w-auto shadow-lg shadow-black/10"
                    >
                        <Plus size={18} /> ADD NEW EVENT
                    </button>
                </div>

                {/* Filters & Search */}
                <div className="bg-white dark:bg-[#1A1A1A] p-4 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm mb-6 flex flex-col md:flex-row gap-4 items-center">
                    <div className="relative flex-1 w-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-black/60 dark:text-white/40" size={18} />
                        <input
                            type="text"
                            placeholder="Search events, venues, locations..."
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm text-black dark:text-white placeholder-black/70 dark:placeholder-white/40"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex gap-2 w-full md:w-auto">
                        <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] text-sm font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-zinc-700 transition-colors">
                            <Filter size={16} /> Filters
                        </button>
                    </div>
                </div>

                {/* Events Table Container */}
                <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-black/5 dark:border-white/5 shadow-sm overflow-hidden">
                    {loading && isInitialLoad ? (
                        <div className="flex items-center justify-center p-20">
                            <Loader2 className="animate-spin text-jazz-brown" size={32} />
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="border-b border-black/5 dark:border-white/5 bg-[#FAFAFA] dark:bg-[#222]">
                                            <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Date</th>
                                            <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Event Detail</th>
                                            <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Location</th>
                                            <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Status</th>
                                            <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-black/5 dark:divide-white/5">
                                        {filteredEvents.length === 0 ? (
                                            <tr>
                                                <td colspan="5" className="px-6 py-10 text-center text-black dark:text-white/50 text-sm">
                                                    {searchTerm ? "No events found matching your search." : "No events scheduled yet."}
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredEvents.map((event) => (
                                                <tr key={event.id} className="hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group">
                                                    <td className="px-6 py-4">
                                                        <div className="flex flex-col">
                                                            <span className="text-xl font-bold text-jazz-brown leading-none" style={{ fontFamily: "Playfair Display, serif" }}>
                                                                {event.date || event.day}
                                                            </span>
                                                            <div className="flex items-center gap-1 mt-1">
                                                                <span className="text-xs font-bold text-black dark:text-white uppercase tracking-wider">{event.month}</span>
                                                                <span className="text-xs text-black dark:text-white/50">{event.year}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <p className="font-semibold text-sm text-black dark:text-white group-hover:text-jazz-brown transition-colors">{event.title}</p>
                                                            {event.ticket_url && (
                                                                <Ticket size={14} className="text-jazz-brown" title="Ticket Link Available" />
                                                            )}
                                                        </div>
                                                        <p className="text-xs text-black dark:text-white/40 mt-1 flex items-center gap-1">
                                                            <MapPin size={12} className="text-jazz-brown" /> {event.venue}
                                                        </p>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-black dark:text-white/60">
                                                        {event.location}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${getStatusStyle(event.status)}`}>
                                                            {event.status === 'On Sale' ? <CheckCircle2 size={12} /> :
                                                                event.status === 'Sold Out' ? <AlertCircle size={12} /> :
                                                                    <Clock size={12} />}
                                                            {event.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <div className="flex justify-end items-center gap-2">
                                                            <button
                                                                onClick={() => setViewModal({ isOpen: true, event })}
                                                                className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-lg text-black dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"
                                                                title="View Details"
                                                            >
                                                                <Eye size={18} />
                                                            </button>
                                                            <button
                                                                onClick={() => navigate(`/admin/events/edit/${event.id}`)}
                                                                className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-lg text-black dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"
                                                                title="Edit"
                                                            >
                                                                <Edit2 size={18} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(event.id)}
                                                                className="p-2 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg text-red-400 hover:text-red-600 transition-colors"
                                                                title="Delete"
                                                            >
                                                                <Trash2 size={18} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Footer actions */}
                            <div className="p-6 border-t border-black/5 dark:border-white/5 flex items-center justify-between">
                                <p className="text-xs text-black dark:text-white/50 font-medium tracking-widest uppercase">Showing {filteredEvents.length} scheduled events</p>
                                <div className="flex gap-2">
                                    <button className="px-4 py-2 text-xs font-bold tracking-widest bg-gray-100 dark:bg-zinc-700 dark:text-white rounded-lg disabled:opacity-50">SYNC WITH CALENDAR</button>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* View Event Modal */}
                <ViewEventModal
                    isOpen={viewModal.isOpen}
                    event={viewModal.event}
                    onClose={() => setViewModal({ isOpen: false, event: null })}
                />
        </div>
    );
}
